"use client";

import { reportClientWarning } from "@/lib/clientReporting";
import { useState, useEffect, useCallback, useMemo, memo, useRef } from "react";
import { useSearchParams } from "next/navigation";
import { useTheme } from "next-themes";
import dynamic from "next/dynamic";
import { Window } from "./Window";
import { Dock } from "./Dock";
import { getStagedWindow, useDesktopStore } from "@/stores/desktopStore";
import { useInboxShipmentRequestStore } from "@/stores/inboxShipmentRequestStore";
import { useWindowManager, useWindowManagerState, buildUrlFromActiveWindow, DESKTOP_PATH, URL_SYNC_DELAY_MS } from "./useWindowManager";
import { DESKTOP_Z_INDEX, SHORTCUT_ICONS_TOP } from "./desktopUI.constants";
import type { SearchResultDocument, SpotlightRequest } from "@/components/GlobalSearchDialog";
import { WindowState } from "./types";
import { closeWindowPane } from "./paneBack.utils";
import { WindowErrorBoundary } from "./WindowErrorBoundary";
import { TopBar, TopBarProvider } from "./TopBar";
import { WarehouseFlowBackground } from "./warehouseFlow/WarehouseFlowBackground";
import { isChatShortcut, openChatWindow, runChatShortcut } from "@/stores/chatAppStore";
import { useSandboxSessionStore } from "@/stores/sandboxSessionStore";
import { SandboxBar } from "@/components/desktop/ui";
import { useAccountMenu } from "./useAccountMenu";
import { DockErrorBoundary } from "./DockErrorBoundary";
import { useClientTranslations } from "@/hooks/useClientTranslations";
import { useSignedInSession } from "@/hooks/useSignedInSession";
import { useDocumentSync } from "@/hooks/useDocumentSync";
import { useWarehouseSchedulingSync } from "@/hooks/useWarehouseSchedulingSync";
import { useIsCartageTeamMember } from "@/hooks/useIsCartageTeamMember";
import { useIsPhone } from "@/hooks/useIsPhone";
import { useDesktopShortcuts } from "./useDesktopShortcuts";
import { cn } from "@/lib/utils";
import { isSlowConnection } from "@/utils/network.utils";
import { WINDOW_MESSAGE_BUNDLES } from "@/i18n/messageBundles";
import { WINDOW_REGISTRY, preloadMessageBundles } from "./windowRegistry";
import { preloadPageCapture } from "./screenReadLoader";
import Image from "next/image";
import { growIslandIntoPalette } from "./viewTransition.utils";
import { WilsonBar } from "./WilsonBar";
import { SelectionBarLayer } from "./SelectionBarLayer";
import { readSelectedPageText, useEditActions, useEditMenuGroups } from "./useEditMenuGroups";
import { useHighlightedTextTracker } from "@/hooks/useHighlightedText";
import { resolveHeroIconSrc } from "./windows/wilsonContentWindows.utils";
import { isMarkedWalletOrg } from "./windows/cartagePay/walletWarm.utils";
import { ContextMenu, type ContextMenuItem, Pressable } from "@/components/desktop/ui";
const loadGlobalSearchDialog = () => import("@/components/GlobalSearchDialog");
const GlobalSearchDialog = dynamic(() => loadGlobalSearchDialog().then(mod => mod.GlobalSearchDialog), {
  ssr: false
} // boolean-naming-exempt: external contract — Next.js dynamic() option
);
const SEARCH_PREFETCH_FALLBACK_MS = 3000;
const prefetchWindowChunks = () => import("./windowPrefetch.constants");
const prefetchWalletWindowChunk = () => import("./walletPrefetch.utils");
const OpenCoachingOnLoad = dynamic(() => import("./OpenCoachingOnLoad").then(mod => mod.OpenCoachingOnLoad), {
  ssr: false
} // boolean-naming-exempt: external contract — Next.js dynamic() option
);
const OpenSetupOnLoad = dynamic(() => import("./OpenSetupOnLoad").then(mod => mod.OpenSetupOnLoad), {
  ssr: false
} // boolean-naming-exempt: external contract — Next.js dynamic() option
);
const DESKTOP_SHORTCUT_KEY_SET = new Set(["[", "w", "W"]);
function isInputElement(element: Element | null): boolean {
  if (!element) return false;
  const tagName = element.tagName.toLowerCase();
  if (tagName === "input" || tagName === "textarea" || tagName === "select") {
    return true;
  }
  return element instanceof HTMLElement && element.isContentEditable;
}
interface ThreadEventDetail {
  threadId: string;
}
function isThreadEventDetail(detail: unknown): detail is ThreadEventDetail {
  if (typeof detail !== "object" || detail === null) return false;
  return "threadId" in detail && typeof detail.threadId === "string";
}
interface DesktopIconTileProps {
  iconSrc: string;
  alt: string;
  label: string;
  ariaLabel: string;
  onClick: () => void;
}
function DesktopIconTile({
  iconSrc,
  alt,
  label,
  ariaLabel,
  onClick
}: DesktopIconTileProps) {
  const {
    resolvedTheme
  } = useTheme();
  return <Pressable className={cn("flex flex-col items-center gap-1 p-1.5 rounded-xl", "hover:bg-ink/[0.05]", "w-[64px] text-center group", "transition-colors duration-fast")} onClick={onClick} aria-label={ariaLabel} data-sentry-element="Pressable" data-sentry-component="DesktopIconTile" data-sentry-source-file="DesktopShell.tsx">
            <div className={cn("w-11 h-11 rounded-xl flex items-center justify-center overflow-hidden", "backdrop-blur-sm shadow-sm", "transition-transform duration-fast", "group-hover:scale-105 group-hover:shadow-md")}>
                <Image src={resolveHeroIconSrc(iconSrc, resolvedTheme === "dark")} alt={alt} width={44} height={44} className="w-full h-full object-contain" data-sentry-element="Image" data-sentry-source-file="DesktopShell.tsx" />
            </div>
            <span className={cn("text-caption font-medium leading-tight line-clamp-2", "text-ink", "transition-colors duration-fast")}>
                {label}
            </span>
        </Pressable>;
}
function DesktopShortcutIcons() {
  const shortcuts = useDesktopShortcuts();
  return shortcuts.map(shortcut => <DesktopIconTile key={shortcut.id} iconSrc={shortcut.iconSrc} alt={shortcut.label} label={shortcut.label} ariaLabel={shortcut.ariaLabel} onClick={shortcut.onOpen} />);
}
const WindowContent = memo(function WindowContent({
  window
}: {
  window: WindowState;
}) {
  const isCartageTeamMember = useIsCartageTeamMember();
  const entry = WINDOW_REGISTRY[window.type];
  if (entry.isCartageTeamOnly && !isCartageTeamMember) return null;
  const Component = entry.load;
  return <Component windowId={window.id} data={window.data} />;
}, (prev, next) => {
  return prev.window.id === next.window.id && prev.window.type === next.window.type && prev.window.data === next.window.data;
});
function useSpotlightState() {
  const [searchOpen, setSearchOpen] = useState(false);
  const [hasOpenedSearch, setHasOpenedSearch] = useState(false);
  const [isSpotlightChat, setIsSpotlightChat] = useState(false);
  const [spotlightRequest, setSpotlightRequest] = useState<SpotlightRequest>({});
  const openWilsonChat = useCallback(({
    threadId,
    question
  }: {
    threadId?: string;
    question?: string;
  }) => openChatWindow({
    threadId,
    question
  }), []);
  const openSpotlight = useCallback((request: SpotlightRequest) => {
    if (request.isChat || request.question) {
      request.onOpening?.();
      setSearchOpen(false);
      openWilsonChat({
        threadId: request.threadId,
        question: request.question
      });
      return;
    }
    growIslandIntoPalette(() => {
      request.onOpening?.();
      setSpotlightRequest(request);
      setSearchOpen(true);
      setHasOpenedSearch(true);
    });
  }, [openWilsonChat]);
  const changeSpotlightOpen = useCallback((isOpen: boolean) => {
    if (isOpen) growIslandIntoPalette(() => setSearchOpen(true));else setSearchOpen(false);
  }, []);
  const ask = useCallback((question: string) => openWilsonChat({
    question
  }), [openWilsonChat]);
  return {
    searchOpen,
    setSearchOpen,
    hasOpenedSearch,
    setHasOpenedSearch,
    isSpotlightChat,
    setIsSpotlightChat,
    spotlightRequest,
    openSpotlight,
    changeSpotlightOpen,
    openWilsonChat,
    ask
  };
}
function DesktopContent({
  spotlight
}: {
  spotlight: ReturnType<typeof useSpotlightState>;
}) {
  const {
    windows,
    openWindow,
    closeWindow,
    stageBack
  } = useWindowManager();
  const tErrorBoundary = useClientTranslations("desktop.errorBoundary");
  const tDocumentDetail = useClientTranslations("desktop.documentDetail");
  const tDock = useClientTranslations("desktop.dock");
  const isPhone = useIsPhone();
  const {
    orgId
  } = useSignedInSession();
  const sandboxSessionId = useSandboxSessionStore(s => s.sandboxSessionId);
  const {
    searchOpen,
    setSearchOpen,
    hasOpenedSearch,
    setHasOpenedSearch,
    isSpotlightChat,
    setIsSpotlightChat,
    spotlightRequest,
    openSpotlight,
    changeSpotlightOpen,
    openWilsonChat,
    ask
  } = spotlight;
  const {
    askAbout
  } = useEditActions({
    openSpotlight,
    ask
  });
  useDocumentSync();
  useWarehouseSchedulingSync();
  useEffect(() => {
    const mountSearchDialog = () => {
      void loadGlobalSearchDialog().then(() => setHasOpenedSearch(true));
      preloadPageCapture();
    };
    const prefetchWallet = () => {
      if (isSlowConnection() || !isMarkedWalletOrg(orgId)) return;
      preloadMessageBundles(WINDOW_MESSAGE_BUNDLES.cartagePay);
      prefetchWalletWindowChunk().catch((error: Error) => reportClientWarning({
        message: "Wallet window chunk prefetch failed",
        cause: error,
        tags: {
          surface: "desktopShell",
          action: "prefetchWalletChunk"
        }
      }));
    };
    if (!("requestIdleCallback" in window)) {
      const id = setTimeout(() => {
        mountSearchDialog();
        prefetchWallet();
      }, SEARCH_PREFETCH_FALLBACK_MS);
      return () => clearTimeout(id);
    }
    const id = requestIdleCallback(() => {
      mountSearchDialog();
      prefetchWallet();
      if (isSlowConnection()) return;
      prefetchWindowChunks().catch((error: Error) => reportClientWarning({
        message: "Window chunk prefetch failed",
        cause: error,
        tags: {
          surface: "desktopShell",
          action: "prefetchWindowChunks"
        }
      }));
    }, {
      timeout: 5000
    });
    return () => cancelIdleCallback(id);
  }, [orgId]);
  const errorBoundaryTranslations = useMemo(() => ({
    title: tErrorBoundary("title"),
    failedToLoad: tErrorBoundary("failedToLoad"),
    genericError: tErrorBoundary("genericError"),
    tryAgain: tErrorBoundary("tryAgain")
  }), [tErrorBoundary]);
  const dockErrorBoundaryTranslations = useMemo(() => ({
    unavailable: tDock("unavailable"),
    retry: tDock("retry")
  }), [tDock]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        openSpotlight({});
      }
      if (isChatShortcut(e)) {
        e.preventDefault();
        runChatShortcut({
          selectedText: readSelectedPageText(),
          askAbout
        });
      }
      if (e.key === "Escape" && !e.defaultPrevented) {
        const active = document.activeElement;
        if (active instanceof HTMLElement && active.closest(".ag-root-wrapper")) {
          e.preventDefault();
          active.blur();
          return;
        }
        const staged = getStagedWindow(useDesktopStore.getState().windows);
        if (staged && !isInputElement(document.activeElement)) {
          e.preventDefault();
          if (!closeWindowPane(staged.id)) stageBack();
          return;
        }
      }
      const activeId = useDesktopStore.getState().activeWindowId;
      if (activeId) {
        if ((e.metaKey || e.ctrlKey) && e.key === "w") {
          e.preventDefault();
          closeWindow(activeId);
          return;
        }
      }
      if (e.metaKey || e.ctrlKey) {
        if (e.key === "[" && isInputElement(document.activeElement)) {
          return;
        }
        if (DESKTOP_SHORTCUT_KEY_SET.has(e.key)) {
          e.preventDefault();
        }
        if (e.key === "[") {
          const staged = getStagedWindow(useDesktopStore.getState().windows);
          if (staged && !closeWindowPane(staged.id)) stageBack();
        } else if (e.key === "w" || e.key === "W") {
          const staged = getStagedWindow(useDesktopStore.getState().windows);
          if (staged) closeWindow(staged.id);
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [stageBack, closeWindow, openSpotlight, openWilsonChat, askAbout]);
  const requestedShipmentDocId = useInboxShipmentRequestStore(state => state.shipmentDocId);
  useEffect(() => {
    if (!requestedShipmentDocId) return;
    openWindow("comms", {
      data: {
        docId: requestedShipmentDocId
      },
      source: "crossWindow"
    });
    useInboxShipmentRequestStore.getState().clearShipmentRequest();
  }, [requestedShipmentDocId, openWindow]);
  useEffect(() => {
    const handleOpenMailWithThread = (event: Event) => {
      if (!(event instanceof CustomEvent)) return;
      const detail: unknown = event.detail;
      if (isThreadEventDetail(detail)) {
        openWindow("comms", {
          data: {
            threadRef: detail.threadId
          },
          source: "crossWindow"
        });
      }
    };
    window.addEventListener("openInboxWithThread", handleOpenMailWithThread);
    return () => {
      window.removeEventListener("openInboxWithThread", handleOpenMailWithThread);
    };
  }, [openWindow]);
  const handleDocumentSelect = useCallback((doc: SearchResultDocument) => {
    if (doc) {
      openWindow("document", {
        title: doc.title || tDocumentDetail("documentFallbackTitle"),
        data: {
          document: doc
        },
        source: "spotlight"
      });
    }
  }, [openWindow, tDocumentDetail]);
  const stagedWindow = useMemo(() => getStagedWindow(windows), [windows]);
  const stagedWindowId = stagedWindow?.id;
  useEffect(() => {
    if (stagedWindowId) setSearchOpen(false);
  }, [stagedWindowId]);
  const openSearch = useCallback(() => openSpotlight({}), [openSpotlight]);
  return <TopBarProvider onOpenSearch={openSearch} onOpenSpotlight={openSpotlight} onOpenConversation={openWilsonChat} isSpotlightOpen={searchOpen} isSpotlightSearching={searchOpen && !isSpotlightChat} data-sentry-element="TopBarProvider" data-sentry-component="DesktopContent" data-sentry-source-file="DesktopShell.tsx">
            {sandboxSessionId !== null && <div className="fixed inset-x-0 top-0 pointer-events-none" style={{
      zIndex: DESKTOP_Z_INDEX.SANDBOX_BAR
    }}>
                    <div className="pointer-events-auto">
                        <SandboxBar />
                    </div>
                </div>}
            <div className="absolute inset-0" style={{
      zIndex: DESKTOP_Z_INDEX.SHELL_BASE
    }}>
                <div className="absolute inset-0" style={{
        zIndex: DESKTOP_Z_INDEX.DESKTOP_ICONS
      }} aria-hidden={stagedWindow !== null} inert={stagedWindow !== null}>
                    <WarehouseFlowBackground isCovered={stagedWindow !== null} data-sentry-element="WarehouseFlowBackground" data-sentry-source-file="DesktopShell.tsx" />
                    <OpenSetupOnLoad isCovered={stagedWindow !== null} openWindow={openWindow} data-sentry-element="OpenSetupOnLoad" data-sentry-source-file="DesktopShell.tsx" />
                </div>
                <div className="absolute inset-0 pointer-events-none" style={{
        zIndex: DESKTOP_Z_INDEX.WINDOWS_BASE
      }}>
                    {windows.map(window => <Window key={window.id} window={window} isStaged={window.id === stagedWindow?.id}>
                            <WindowErrorBoundary windowTitle={window.title} translations={errorBoundaryTranslations} windowType={window.type} windowId={window.id}>
                                <WindowContent window={window} />
                            </WindowErrorBoundary>
                        </Window>)}
                </div>

                {!isPhone && <div className="fixed right-4 pointer-events-auto flex flex-col items-center gap-1" style={{
        top: SHORTCUT_ICONS_TOP,
        zIndex: DESKTOP_Z_INDEX.SHORTCUTS
      }}>
                        <DesktopShortcutIcons />
                    </div>}
            </div>

            <DockErrorBoundary translations={dockErrorBoundaryTranslations} data-sentry-element="DockErrorBoundary" data-sentry-source-file="DesktopShell.tsx">
                <Dock data-sentry-element="Dock" data-sentry-source-file="DesktopShell.tsx" />
            </DockErrorBoundary>

            <TopBar data-sentry-element="TopBar" data-sentry-source-file="DesktopShell.tsx" />

            <SelectionBarLayer openSpotlight={openSpotlight} ask={ask} data-sentry-element="SelectionBarLayer" data-sentry-source-file="DesktopShell.tsx" />

            {hasOpenedSearch && <GlobalSearchDialog open={searchOpen} onOpenChange={changeSpotlightOpen} onDocumentSelect={handleDocumentSelect} request={spotlightRequest} onChatModeChange={setIsSpotlightChat} onOpenChat={question => openWilsonChat({
      question
    })} />}
        </TopBarProvider>;
}
export function DesktopShell() {
  const searchParams = useSearchParams();
  const windowManagerState = useWindowManagerState({
    searchParams
  });
  const tContextMenu = useClientTranslations("desktop.contextMenu");
  const tA11y = useClientTranslations("desktop.a11y");
  const {
    setTheme
  } = useTheme();
  const {
    orgGroup,
    themeGroup,
    logoutGroup
  } = useAccountMenu();
  useHighlightedTextTracker();
  const spotlight = useSpotlightState();
  const {
    closeAllWindows,
    windows,
    activeWindowId
  } = windowManagerState;
  const hasWindows = windows.length > 0;
  const initialSyncDoneRef = useRef(false);
  const activeWindowUrl = useMemo(() => {
    return buildUrlFromActiveWindow(windows, activeWindowId);
  }, [windows, activeWindowId]);
  useEffect(() => {
    const timer = setTimeout(() => {
      initialSyncDoneRef.current = true;
    }, URL_SYNC_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);
  useEffect(() => {
    if (!initialSyncDoneRef.current) return;
    const newUrl = activeWindowUrl ? `${DESKTOP_PATH}?${activeWindowUrl}` : DESKTOP_PATH;
    window.history.replaceState(null, "", newUrl);
  }, [activeWindowUrl]);
  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    if (!savedTheme) {
      setTheme("light");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const buildEditGroups = useEditMenuGroups({
    openSpotlight: spotlight.openSpotlight,
    ask: spotlight.ask
  });
  const contextMenuGroups: ContextMenuItem[][] = [orgGroup, themeGroup, [{
    label: tContextMenu("closeAllWindows"),
    onSelect: closeAllWindows,
    disabled: !hasWindows // boolean-naming-exempt: external contract — desktop ui Menu item field on main
  }], logoutGroup];
  return <ContextMenu groups={contextMenuGroups} label={tA11y("desktopMenu")} buildLeadingGroups={buildEditGroups} data-sentry-element="ContextMenu" data-sentry-component="DesktopShell" data-sentry-source-file="DesktopShell.tsx">
            <div className="fixed inset-0 overflow-hidden desktop-background">
                <div className="relative h-full w-full">
                    <DesktopContent spotlight={spotlight} data-sentry-element="DesktopContent" data-sentry-source-file="DesktopShell.tsx" />
                </div>

                <WilsonBar data-sentry-element="WilsonBar" data-sentry-source-file="DesktopShell.tsx" />
                <OpenCoachingOnLoad data-sentry-element="OpenCoachingOnLoad" data-sentry-source-file="DesktopShell.tsx" />
            </div>
        </ContextMenu>;
}